import { inject, Injectable } from "@angular/core";
import { ColDef } from "ag-grid-community";

// EZ
import { CrudFilter, CrudMatchType } from "@template-ui/types";

// App
import { AppApis } from "@app/core/APIs/app-apis";
import { AppGridColumnTypes } from "@app/shared/components/tables/ag-grid-cells/app-grid-column-types";
import {
  ActivityLogAddActivityEndpointParamConfig,
  ActivityLogContext,
  ActivityLogGridVariantConfig,
  ActivityLogType,
  DEFAULT_ACTIVITY_LOG_COL_DEFS
} from "./activity-log-grid.types";

/**
 * Centralizes Activity Log grid configuration by variant.
 *
 * Provides endpoints, document-type source, and default column definitions
 * for each `ActivityLogType`.
 */
@Injectable({ providedIn: "root" })
export class ActivityLogGridConfigService {
  private readonly apis = inject(AppApis);
  private readonly addActivityEndpointParamsByType: Record<ActivityLogType, ActivityLogAddActivityEndpointParamConfig[]> = {
    [ActivityLogType.CommutationInvestigation]: [
      { contextKey: "caseId", formDataKey: "CaseId", required: true },
    ],
    [ActivityLogType.BphFullInvestigation]: [
      { contextKey: "caseId", formDataKey: "CaseId", required: true },
    ],
    [ActivityLogType.LegalDecisionReview]: [
      { contextKey: "cdcNum", formDataKey: "CdcNum", required: true },
      { contextKey: "planId", formDataKey: "PlanId", required: true },
      { contextKey: "partId", formDataKey: "PartId", required: true}
    ],
  };
  private readonly legalDecisionReviewColDefs: ColDef[] = [
    { field: "addDate", headerName: "Date", type: AppGridColumnTypes.DateOnlyColumn },
    { field: "dateFrom", hide: true },
    { field: "dateTo", hide: true },
    { field: "addDate", headerName: "Time", type: AppGridColumnTypes.FormatTimeOnlyColumn, maxWidth: 150 },
    { field: "activityDescription", headerName: "Activity", minWidth: 300 },
    { field: "workflow", headerName: "Workflow", minWidth: 300 },
    { field: "bitsUser", headerName: "User", maxWidth: 200 },
  ];

  /**
   * Resolves activity-log config for the requested type and context.
   */
  public getConfig(type: ActivityLogType, context: ActivityLogContext): ActivityLogGridVariantConfig {
    switch (type) {
      case ActivityLogType.CommutationInvestigation: {
        const caseId = this.getRequiredNumberContextValue(context, "caseId", type);
        return {
          settingsKey: "ActivityLog:CommutationInvestigation.GridConfig",
          readEndpoint: this.apis.core.getClemencyCommutationActivityLogByCaseIdBySortByBySortDir(caseId, "addDate", "desc"),
          addActivityEndpoint: this.apis.core.postClemencyAddCommutationActivityLogAndDocument(),
          addActivityEndpointParams: this.getAddActivityEndpointParams(type),
          documentTypesModule: "COMM_INV",
          documentTypesEndpoint: this.apis.core.getDocumentGetDocumentTypesForUploadByModule("COMM_INV"),
          colDefs: [...DEFAULT_ACTIVITY_LOG_COL_DEFS],
        };
      }

      case ActivityLogType.BphFullInvestigation: {
        const caseId = this.getRequiredNumberContextValue(context, "caseId", type);
        return {
          settingsKey: "ActivityLog:BphFullInvestigation.GridConfig",
          readEndpoint: this.apis.core.getInvestigationInvestigationActivityLogByCaseIdBySortByBySortDir(caseId, "addDate", "desc"),
          addActivityEndpoint: this.apis.core.postInvestigationAddInvestigationActivityLogAndDocument(),
          addActivityEndpointParams: this.getAddActivityEndpointParams(type),
          documentTypesModule: "BPH_FULL_INV",
          documentTypesEndpoint: this.apis.core.getDocumentGetDocumentTypesForUploadByModule("BPH_FULL_INV"),
          colDefs: [...DEFAULT_ACTIVITY_LOG_COL_DEFS],
        };
      }

      case ActivityLogType.LegalDecisionReview: {
        const cdcNum = this.getRequiredStringContextValue(context, "cdcNum", type);
        return {
          settingsKey: "ActivityLog:LegalDecisionReview.GridConfig",
          readEndpoint: this.apis.core.postActivityLogAllActivityLog(),
          readEndpointProtocol: "POST_JSON",
          staticFilters: {
            cdcNum: {
              name: "cdcNum",
              matchType: CrudMatchType.Contains,
              value: cdcNum,
            } as CrudFilter,
            bitsModule: {
              name: "bitsModule",
              value: "LEGAL",
            } as CrudFilter,
            removeAlerts: {
              name: "removeAlerts",
              value: true,
            } as CrudFilter,
          },
          filterInputConfigs: [
            { name: "dateFrom", type: "date", label: "Date From" },
            { name: "dateTo", type: "date", label: "Date To" },
            {
              name: "workflow",
              type: "select",
              label: "Workflow",
              optionsEndpoint: this.apis.core.getActivityLogActivityLogWorkflowFilterByModule("LEGAL"),
              optionsDisplayField: "workflow",
              optionslValueField: "workflow",
            },
            {
              name: "bitsUser",
              type: "select",
              label: "User",
              optionsEndpoint: this.apis.core.getActivityLogActivityLogUserFilterByCdcNumByModule(cdcNum, "LEGAL"),
              optionsDisplayField: "userName",
              optionslValueField: "partId",
            },
          ],
          // enable this if manually adding activity logs is supposed to be enabled
          // addActivityEndpoint: this.apis.legal.postActivityLog_CreateActivityLog(),
          // addActivityEndpointParams: this.getAddActivityEndpointParams(type),
          documentTypesModule: "LEGAL",
          //documentTypesEndpoint: this.apis.core.getDocumentGetDocumentTypesForUploadByModule("LEGAL"),
          colDefs: [...this.legalDecisionReviewColDefs],
        };
      }
    }
  }

  private getRequiredNumberContextValue(context: ActivityLogContext, key: string, type: ActivityLogType): number {
    const value = context[key];
    const parsed = Number(value);
    if (!value || Number.isNaN(parsed)) {
      throw new Error(`ActivityLogGridConfigService: '${key}' is required for ActivityLogType '${type}'.`);
    }

    return parsed;
  }

  private getRequiredStringContextValue(context: ActivityLogContext, key: string, type: ActivityLogType): string {
    const value = context[key];
    if (typeof value !== "string" || !value.trim().length) {
      throw new Error(`ActivityLogGridConfigService: '${key}' is required for ActivityLogType '${type}'.`);
    }

    return value;
  }

  private getAddActivityEndpointParams(type: ActivityLogType): ActivityLogAddActivityEndpointParamConfig[] {
    return this.addActivityEndpointParamsByType[type] ?? [];
  }
}
